import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  DpPayrollBreakdown,
  DpPayrollBreakdownItem,
  DpPayrollPeriod,
  DpPayrollResultStatus,
  getPayrollBreakdownApi,
  getPayrollBreakdownItemApi,
  PAYROLL_RESULT_STATUS_LABELS,
} from "../api/payrollApi";
import { formatCurrency } from "../utils/format";

/** Card dos totais da folha que abre este modal. */
export type PayrollTotalsCard = "contracts" | "gross" | "deductions" | "advance" | "net" | "inss" | "irrf" | "fgts";

/** Cards que são uma rubrica só: abrem direto a lista por colaborador. */
const SINGLE_ITEM: Partial<Record<PayrollTotalsCard, { code: string; title: string }>> = {
  advance: { code: "ADIANTAMENTO", title: "Adiantamento (já pago)" },
  inss: { code: "INSS", title: "INSS" },
  irrf: { code: "IRRF", title: "IRRF" },
  fgts: { code: "FGTS", title: "FGTS (informativo)" },
};

const TITLES: Record<PayrollTotalsCard, string> = {
  contracts: "Colaboradores",
  gross: "Bruto — proventos",
  deductions: "Descontos",
  advance: "Adiantamento (já pago)",
  net: "Líquido final",
  inss: "INSS",
  irrf: "IRRF",
  fgts: "FGTS (informativo)",
};

const formatReference = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 4 });

type Props = {
  token: string;
  period: DpPayrollPeriod;
  card: PayrollTotalsCard;
  onClose: () => void;
  onOpenResult: (contractId: string) => void;
};

/**
 * O que forma cada total da folha: as rubricas somadas e, ao clicar numa, quem a recebe.
 * Mesma base dos cards — quem está sem apuração fica fora.
 */
export default function PayrollTotalsModal({ token, period, card, onClose, onOpenResult }: Props) {
  const single = SINGLE_ITEM[card];
  const [breakdown, setBreakdown] = useState<DpPayrollBreakdown | null>(null);
  const [item, setItem] = useState<DpPayrollBreakdownItem | null>(null);
  const [itemCode, setItemCode] = useState<string | null>(single?.code ?? null);
  const [loading, setLoading] = useState(false);

  const needsBreakdown = card === "gross" || card === "deductions" || card === "net";

  useEffect(() => {
    if (!needsBreakdown) return;
    let alive = true;
    setLoading(true);
    getPayrollBreakdownApi(token, period.id)
      .then((b) => alive && setBreakdown(b))
      .catch((e: Error) => toast.error(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [needsBreakdown, token, period.id]);

  useEffect(() => {
    if (!itemCode) {
      setItem(null);
      return;
    }
    let alive = true;
    setLoading(true);
    getPayrollBreakdownItemApi(token, period.id, itemCode)
      .then((i) => alive && setItem(i))
      .catch((e: Error) => toast.error(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [itemCode, token, period.id]);

  const s = period.summary;
  const proventos = breakdown?.items.filter((i) => i.kind === "PROVENTO") ?? [];
  const descontos = breakdown?.items.filter((i) => i.kind === "DESCONTO") ?? [];
  const drilled = !!itemCode && !single;

  const groupTable = (rows: DpPayrollBreakdown["items"], total: string, totalLabel: string) => (
    <table className="dpTable">
      <thead>
        <tr>
          <th>Rubrica</th>
          <th style={{ textAlign: "right" }}>Colaboradores</th>
          <th style={{ textAlign: "right" }}>Valor</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={`${r.kind}:${r.itemCode}`}
           
            style={{ cursor: "pointer" }}
            onClick={() => setItemCode(r.itemCode)}
            title="Ver quem recebe esta rubrica"
          >
            <td>{r.itemName}</td>
            <td style={{ textAlign: "right" }}>{r.count}</td>
            <td style={{ textAlign: "right" }}>{formatCurrency(r.amount)}</td>
          </tr>
        ))}
        {rows.length === 0 ? (
          <tr>
            <td colSpan={3}>Nenhuma rubrica.</td>
          </tr>
        ) : null}
      </tbody>
      <tfoot>
        <tr>
          <th>{totalLabel}</th>
          <th />
          <th style={{ textAlign: "right" }}>{formatCurrency(total)}</th>
        </tr>
      </tfoot>
    </table>
  );

  const body = () => {
    if (card === "contracts") {
      return (
        <table className="dpTable">
          <tbody>
            {(Object.keys(PAYROLL_RESULT_STATUS_LABELS) as DpPayrollResultStatus[])
              .filter((st) => (s.byStatus[st] ?? 0) > 0)
              .map((st) => (
                <tr key={st}>
                  <td>{PAYROLL_RESULT_STATUS_LABELS[st]}</td>
                  <td style={{ textAlign: "right" }}>{s.byStatus[st]}</td>
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Total</th>
              <th style={{ textAlign: "right" }}>{s.contracts}</th>
            </tr>
          </tfoot>
        </table>
      );
    }

    if (itemCode) {
      if (loading || !item) return <p>Carregando...</p>;
      return (
        <>
          <p style={{ margin: 0 }}>
            <strong>{item.rows.length}</strong> colaborador(es) · total <strong>{formatCurrency(item.total)}</strong>.
            Clique num colaborador para abrir o cálculo dele.
          </p>
          <table className="dpTable">
            <thead>
              <tr>
                <th>Colaborador</th>
                <th style={{ textAlign: "right" }}>Ref.</th>
                <th style={{ textAlign: "right" }}>Valor</th>
                <th>Memória de cálculo</th>
              </tr>
            </thead>
            <tbody>
              {item.rows.map((r) => (
                <tr key={r.contractId} style={{ cursor: "pointer" }} onClick={() => onOpenResult(r.contractId)}>
                  <td>
                    {r.meta.fullName}
                    {r.meta.store ? <div style={{ fontSize: 12, opacity: 0.7 }}>{r.meta.store}</div> : null}
                  </td>
                  <td style={{ textAlign: "right" }}>{formatReference(r.reference)}</td>
                  <td style={{ textAlign: "right" }}>{formatCurrency(r.amount)}</td>
                  <td style={{ fontSize: 12 }}>{r.memo}</td>
                </tr>
              ))}
              {item.rows.length === 0 ? (
                <tr>
                  <td colSpan={4}>Ninguém com esta rubrica no mês.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </>
      );
    }

    if (loading || !breakdown) return <p>Carregando...</p>;

    if (card === "gross") return groupTable(proventos, breakdown.totals.proventos, "Bruto");
    if (card === "deductions") return groupTable(descontos, breakdown.totals.descontos, "Total de descontos");

    // Líquido: a conta inteira, com o adiantamento destacado (ele já foi pago e está nos descontos).
    return (
      <>
        <table className="dpTable">
          <tbody>
            <tr>
              <td>Bruto (proventos)</td>
              <td style={{ textAlign: "right" }}>{formatCurrency(s.gross)}</td>
            </tr>
            <tr>
              <td>− Descontos{Number(s.advance) > 0 ? ` (inclui o adiantamento de ${formatCurrency(s.advance)}, já pago)` : ""}</td>
              <td style={{ textAlign: "right" }}>{formatCurrency(s.deductions)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th>= Líquido final</th>
              <th style={{ textAlign: "right" }}>{formatCurrency(s.net)}</th>
            </tr>
          </tfoot>
        </table>
        <h3 style={{ margin: "8px 0 0", fontSize: 15 }}>Proventos</h3>
        {groupTable(proventos, breakdown.totals.proventos, "Bruto")}
        <h3 style={{ margin: "8px 0 0", fontSize: 15 }}>Descontos</h3>
        {groupTable(descontos, breakdown.totals.descontos, "Total de descontos")}
      </>
    );
  };

  const title = drilled ? item?.itemName ?? "Rubrica" : TITLES[card];

  return (
    <div className="dpModalOverlay">
      <div className="dpModal dpModal--wide">
        <header className="dpModal__header">
          <h2 className="dpModal__title">{title}</h2>
          <button className="dpModal__close" onClick={onClose} aria-label="Fechar">
            &times;
          </button>
        </header>
        <div className="dpModal__body">
          {s.withoutInput > 0 && card !== "contracts" ? (
            <p className="dpNotice dpNotice--warning" style={{ margin: 0 }}>
              {s.withoutInput} colaborador(es) sem apuração do mês ficam fora, como nos totais.
            </p>
          ) : null}
          {body()}
        </div>
        <footer className="dpModal__footer">
          {drilled ? (
            <button className="dpBtn dpBtn--secondary" onClick={() => setItemCode(null)}>
              ← Voltar para as rubricas
            </button>
          ) : null}
          <button className="dpBtn dpBtn--secondary" onClick={onClose}>
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}
